/**
 * BookingService
 * Service for managing bookings with approval workflows, status transitions,
 * compliance tracking, dashboard stats, calendar events, and view endpoints
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for booking CRUD operations with approval workflows, status transitions,
 * compliance management, dashboard analytics, and calendar integration
 */
export class BookingService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? BookingService.DEFAULT_SERVICE_PATH,
            basePath: BookingService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== BOOKING QUERIES ====================
    /**
     * Get all bookings for the current authenticated user
     * @returns Array of the current user's bookings
     */
    async getMyBookings() {
        const response = await this.getMyBookingsRaw();
        return response.data ?? [];
    }
    /**
     * Get all bookings for the current authenticated user with full response
     * @returns ResponseData with array of bookings
     */
    getMyBookingsRaw() {
        const url = this.buildUrl('my-bookings');
        return this.http.get(url);
    }
    /**
     * Get all bookings across all users (admin)
     * @returns Array of all bookings
     */
    async getAllBookings() {
        const response = await this.getAllBookingsRaw();
        return response.data ?? [];
    }
    /**
     * Get all bookings across all users with full response
     * @returns ResponseData with array of all bookings
     */
    getAllBookingsRaw() {
        const url = this.buildUrl('all-bookings');
        return this.http.get(url);
    }
    /**
     * Get all currently active bookings
     * @returns Array of active bookings
     */
    async getActive() {
        const response = await this.getActiveRaw();
        return response.data ?? [];
    }
    /**
     * Get all currently active bookings with full response
     * @returns ResponseData with array of active bookings
     */
    getActiveRaw() {
        const url = this.buildUrl('active');
        return this.http.get(url);
    }
    /**
     * Get all pending bookings awaiting approval
     * @returns Array of pending bookings
     */
    async getPending() {
        const response = await this.getPendingRaw();
        return response.data ?? [];
    }
    /**
     * Get all pending bookings awaiting approval with full response
     * @returns ResponseData with array of pending bookings
     */
    getPendingRaw() {
        const url = this.buildUrl('pending');
        return this.http.get(url);
    }
    /**
     * Get all overdue bookings (past end time, not completed/returned)
     * @returns Array of overdue bookings
     */
    async getOverdue() {
        const response = await this.getOverdueRaw();
        return response.data ?? [];
    }
    /**
     * Get all overdue bookings with full response
     * @returns ResponseData with array of overdue bookings
     */
    getOverdueRaw() {
        const url = this.buildUrl('overdue');
        return this.http.get(url);
    }
    // ==================== APPROVAL WORKFLOW ====================
    /**
     * Approve a pending booking
     * @param bookingId - The booking ID to approve
     * @param request - Approval request with optional notes
     * @returns Updated booking
     */
    async approve(bookingId, request) {
        const response = await this.approveRaw(bookingId, request);
        if (!response.data) {
            throw new Error(`Failed to approve booking ${bookingId}`);
        }
        return response.data;
    }
    /**
     * Approve a pending booking with full response
     * @param bookingId - The booking ID to approve
     * @param request - Approval request with optional notes
     * @returns ResponseData with updated booking
     */
    approveRaw(bookingId, request) {
        const url = this.buildUrl(`${bookingId}/approve`);
        return this.http.put(url, request);
    }
    /**
     * Reject a pending booking
     * @param bookingId - The booking ID to reject
     * @param request - Rejection request with reason
     * @returns Updated booking
     */
    async reject(bookingId, request) {
        const response = await this.rejectRaw(bookingId, request);
        if (!response.data) {
            throw new Error(`Failed to reject booking ${bookingId}`);
        }
        return response.data;
    }
    /**
     * Reject a pending booking with full response
     * @param bookingId - The booking ID to reject
     * @param request - Rejection request with reason
     * @returns ResponseData with updated booking
     */
    rejectRaw(bookingId, request) {
        const url = this.buildUrl(`${bookingId}/reject`);
        return this.http.put(url, request);
    }
    // ==================== STATUS TRANSITIONS ====================
    /**
     * Start a booking (mark as in-progress)
     * @param bookingId - The booking ID to start
     * @returns Updated booking
     */
    async start(bookingId) {
        const response = await this.startRaw(bookingId);
        if (!response.data) {
            throw new Error(`Failed to start booking ${bookingId}`);
        }
        return response.data;
    }
    /**
     * Start a booking with full response
     * @param bookingId - The booking ID to start
     * @returns ResponseData with updated booking
     */
    startRaw(bookingId) {
        const url = this.buildUrl(`${bookingId}/start`);
        return this.http.put(url, {});
    }
    /**
     * Complete a booking (mark as completed/returned)
     * @param bookingId - The booking ID to complete
     * @returns Updated booking
     */
    async complete(bookingId) {
        const response = await this.completeRaw(bookingId);
        if (!response.data) {
            throw new Error(`Failed to complete booking ${bookingId}`);
        }
        return response.data;
    }
    /**
     * Complete a booking with full response
     * @param bookingId - The booking ID to complete
     * @returns ResponseData with updated booking
     */
    completeRaw(bookingId) {
        const url = this.buildUrl(`${bookingId}/complete`);
        return this.http.put(url, {});
    }
    /**
     * Cancel a booking
     * @param bookingId - The booking ID to cancel
     * @returns Updated booking
     */
    async cancel(bookingId) {
        const response = await this.cancelRaw(bookingId);
        if (!response.data) {
            throw new Error(`Failed to cancel booking ${bookingId}`);
        }
        return response.data;
    }
    /**
     * Cancel a booking with full response
     * @param bookingId - The booking ID to cancel
     * @returns ResponseData with updated booking
     */
    cancelRaw(bookingId) {
        const url = this.buildUrl(`${bookingId}/cancel`);
        return this.http.put(url, {});
    }
    // ==================== COMPLIANCE ====================
    /**
     * Get list of non-compliant user IDs (users with overdue/policy-violating bookings)
     * @returns Array of user ID strings
     */
    async getNonCompliantUsers() {
        const response = await this.getNonCompliantUsersRaw();
        return response.data ?? [];
    }
    /**
     * Get list of non-compliant user IDs with full response
     * @returns ResponseData with array of user ID strings
     */
    getNonCompliantUsersRaw() {
        const url = this.buildUrl('non-compliant-users');
        return this.http.get(url);
    }
    /**
     * Override the booking block for a non-compliant user
     * @param userId - The user ID to unblock
     * @returns True if the override was successful
     */
    async overrideBlock(userId) {
        const response = await this.overrideBlockRaw(userId);
        return response.data ?? false;
    }
    /**
     * Override the booking block for a non-compliant user with full response
     * @param userId - The user ID to unblock
     * @returns ResponseData with boolean result
     */
    overrideBlockRaw(userId) {
        const url = this.buildUrl(`override-block/${userId}`);
        return this.http.put(url, {});
    }
    // ==================== DASHBOARD & CALENDAR ====================
    /**
     * Get booking dashboard statistics
     * @returns Dashboard statistics for bookings
     */
    async getDashboardStats() {
        const response = await this.getDashboardStatsRaw();
        if (!response.data) {
            throw new Error('Failed to retrieve booking dashboard stats');
        }
        return response.data;
    }
    /**
     * Get booking dashboard statistics with full response
     * @returns ResponseData with dashboard statistics
     */
    getDashboardStatsRaw() {
        const url = this.buildUrl('dashboard/stats');
        return this.http.get(url);
    }
    /**
     * Get booking calendar events for a date range
     * @param startDate - Optional start date as epoch timestamp
     * @param endDate - Optional end date as epoch timestamp
     * @returns Array of booking calendar events
     */
    async getCalendarEvents(startDate, endDate) {
        const response = await this.getCalendarEventsRaw(startDate, endDate);
        return response.data ?? [];
    }
    /**
     * Get booking calendar events for a date range with full response
     * @param startDate - Optional start date as epoch timestamp
     * @param endDate - Optional end date as epoch timestamp
     * @returns ResponseData with array of booking calendar events
     */
    getCalendarEventsRaw(startDate, endDate) {
        const url = this.buildUrlWithParams('calendar-events', {
            start: startDate,
            end: endDate,
        });
        return this.http.get(url);
    }
    // ==================== VIEW ENDPOINTS ====================
    /**
     * Get booking view by ID
     * @param id - The booking ID
     * @returns Booking model view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`Booking view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get booking view by ID with full response
     * @param id - The booking ID
     * @returns ResponseData with booking model view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all booking list views
     * @returns Array of booking model views
     */
    async getListView() {
        const response = await this.getListViewRaw();
        return response.data ?? [];
    }
    /**
     * Get all booking list views with full response
     * @returns ResponseData with array of booking model views
     */
    getListViewRaw() {
        const url = this.buildUrl('list-view');
        return this.http.get(url);
    }
    /**
     * Get booking detail view by ID (includes all related data)
     * @param id - The booking ID
     * @returns Booking detail model view with all related data
     */
    async getDetailView(id) {
        const response = await this.getDetailViewRaw(id);
        if (!response.data) {
            throw new Error(`Booking detail view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get booking detail view by ID with full response
     * @param id - The booking ID
     * @returns ResponseData with booking detail model view
     */
    getDetailViewRaw(id) {
        const url = this.buildUrl(`${id}/detail`);
        return this.http.get(url);
    }
}
BookingService.DEFAULT_SERVICE_PATH = 'booking-management';
BookingService.BASE_PATH = 'api/bookings';
//# sourceMappingURL=BookingService.js.map